import type { AnnotationsOptions, Chart, Options, Point, SVGElement } from "highcharts";
import { renderToString } from "react-dom/server";
import styledComponents from "styled-components";

interface CustomChart extends Chart {
    customHoverRects?: Array<{ rect: SVGElement; index: number }>;
}

import { tooltipContainerConfig } from "@similarweb/pro-logic-components";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import type { ColumnDefinitionWithoutTitle } from "@similarweb/ui-components";
import { ChartTooltip } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter, spendFilter } from "app/filters/ngFilters";

import { EDataType } from "../../../advertisers/overview/components/types";
import type { AdIntelligenceController } from "../../shared/types/types";

const TooltipTitle = styledComponents.span`
    ${setFontVariant("subtitle1", colorsPalettes.carbon[500])};
`;

interface ITooltipData {
    label: {
        value: string;
        color: string;
    };
    percentage: {
        value: string;
    };
    absolute: {
        value: string;
    };
}

const tooltipColumnDefinition: ColumnDefinitionWithoutTitle<ITooltipData>[] = [
    {
        key: "0",
        selector: "label" as const,
        align: "left" as const,
        size: "120px",
        render: ({ label }: { label: ITooltipData["label"] }) => (
            <ChartTooltip.LabelCell
                label={label.value}
                backgroundColor={label.color}
            />
        ),
    },
    {
        key: "1",
        selector: "percentage" as const,
        align: "right" as const,
        size: "60px",
        render: ({ percentage }: { percentage: ITooltipData["percentage"] }) => (
            <ChartTooltip.ValueCell value={percentage.value} />
        ),
    },
    {
        key: "2",
        selector: "absolute" as const,
        align: "right" as const,
        size: "80px",
        render: ({ absolute }: { absolute: ITooltipData["absolute"] }) => (
            <ChartTooltip.ValueCell value={absolute.value} />
        ),
    },
];

interface ICreateGraphConfigParams {
    controller: AdIntelligenceController;
    thirdMetric: EDataType;
    totals: {
        spend: number;
        impressions: number;
        [key: string]: number;
    };
    performanceData: Record<string, any>;
}

export const createGraphConfig = ({
    controller,
    thirdMetric,
    totals,
    performanceData,
}: ICreateGraphConfigParams): Options => {
    const metrics = [
        translate(`ad.intelligence.${controller}.overview.performance.widget.spend`),
        translate(`ad.intelligence.${controller}.overview.performance.widget.impressions`),
        translate(`ad.intelligence.${controller}.overview.performance.widget.${thirdMetric}`),
    ];

    const annotations: AnnotationsOptions[] = [];
    const metricTotals = [totals.spend, totals.impressions, totals[thirdMetric]];

    metricTotals.forEach((total, index) => {
        if (total === 0) {
            annotations.push({
                draggable: "",
                labelOptions: {
                    shape: "rect",
                    backgroundColor: "transparent",
                    borderWidth: 0,
                    style: {
                        fontSize: "11px",
                        color: colorsPalettes.carbon[300],
                    },
                },
                labels: [
                    {
                        point: {
                            x: index,
                            y: 4,
                            xAxis: 0,
                            yAxis: 0,
                        },
                        text: translate("common.nodata"),
                        y: 0,
                        verticalAlign: "top",
                    },
                ],
            });
        }
    });

    const tooltipFormatter = function (this: Point): string {
        const thisPoint = this as unknown as { x?: number; points?: Point[] };
        const points = thisPoint.points;
        const xValue = thisPoint.x;
        const categoryIndex = typeof xValue === "number" ? xValue : 0;
        const metricName = metrics[categoryIndex] || "";
        const metricKeys = [EDataType.SPEND, EDataType.IMPRESSIONS, thirdMetric] as const;
        const metricKey = metricKeys[categoryIndex];

        if (!points || !metricKey) {
            return "";
        }

        const data: ITooltipData[] = points
            .map((point) => {
                const domain = point.series?.name || "";
                const domainData = performanceData[domain];

                if (!domainData || !domainData[metricKey]) {
                    return null;
                }

                const metricData = domainData[metricKey];
                if (!metricData || typeof metricData.value !== "number") {
                    return null;
                }

                const absoluteValue = metricData.value;
                const formattedAbsolute: string =
                    metricKey === EDataType.SPEND
                        ? String(spendFilter(absoluteValue, abbrNumberFilter()))
                        : String(abbrNumberFilter()(absoluteValue));

                const pointY = typeof point.y === "number" ? point.y : 0;
                const pointColor = point.color;

                return {
                    label: {
                        value: domain,
                        color: typeof pointColor === "string" ? pointColor : colorsPalettes.black[0],
                    },
                    percentage: {
                        value: `${pointY.toFixed(0)}%`,
                    },
                    absolute: {
                        value: formattedAbsolute,
                    },
                };
            })
            .filter((item): item is ITooltipData => item !== null);

        return renderToString(
            <ChartTooltip
                withoutColumnTitles
                width={300}
                title={<TooltipTitle>{metricName}</TooltipTitle>}
                columnDefinition={tooltipColumnDefinition}
                data={data}
            />,
        );
    };

    return {
        chart: {
            height: "240px",
            events: {
                load() {
                    const customChart = this as CustomChart;
                    const xAxis = customChart.xAxis[0];

                    customChart.customHoverRects = metrics.map((_, index) => {
                        const hasData = metricTotals[index] !== 0;
                        const rect = customChart.renderer
                            .rect(0, 0, 0, 0, 0)
                            .attr({ fill: "transparent", "stroke-width": 0, zIndex: 5 })
                            .css({ cursor: hasData ? "pointer" : "default" })
                            .on("mousemove", function () {
                                // Don't show tooltip for columns with no data
                                if (!hasData) {
                                    return;
                                }
                                const points = customChart.series
                                    .map((series) => series.points[index])
                                    .filter((point) => point && point.visible !== false);
                                if (points.length > 0 && points[0]) {
                                    points[0].onMouseOver();
                                }
                            })
                            .on("mouseout", () => {
                                customChart.pointer.reset();
                            })
                            .add();

                        const tickPos = xAxis.toPixels(index, false);
                        const tickWidth = xAxis.toPixels(0.5, false) - xAxis.toPixels(-0.5, false);

                        rect.attr({
                            x: tickPos - tickWidth / 2,
                            y: customChart.plotTop,
                            width: tickWidth,
                            height: customChart.plotHeight,
                        });

                        return { rect, index };
                    });
                },
            },
        },
        xAxis: {
            categories: metrics,
            crosshair: true,
        },
        yAxis: {
            min: 0,
            max: 100,
            tickPositions: [0, 25, 50, 75, 100],
            labels: {
                formatter: function () {
                    return `${this.value}%`;
                },
            },
        },
        plotOptions: {
            column: {
                stacking: null,
                grouping: true,
                shadow: false,
                borderWidth: 0,
                pointPadding: 0.05,
                states: {
                    hover: {
                        enabled: false,
                    },
                },
            },
        },
        tooltip: {
            enabled: true,
            shared: true,
            outside: true,
            useHTML: true,
            ...tooltipContainerConfig().tooltip,
            formatter: tooltipFormatter,
            positioner: function (labelWidth, _, point) {
                const chart = this.chart;
                const xAxis = chart.xAxis[0];
                const plotLeft = chart.plotLeft;
                const plotWidth = chart.plotWidth;

                // Calculate the width of the entire column group category
                const categoryWidth = xAxis.toPixels(0.5, false) - xAxis.toPixels(-0.5, false);
                const horizontalOffset = categoryWidth / 2 + 8;

                // Get all hovered points in the group and use the rightmost column for positioning
                const hoverPoints = chart.hoverPoints || [];
                const rightmostPoint = hoverPoints.length > 0 ? hoverPoints[hoverPoints.length - 1] : point;

                const pointX = rightmostPoint.plotX || point.plotX;

                // Position tooltip to the right of the column group, or to the left if it would overflow
                let x: number;
                if (pointX + labelWidth > plotWidth) {
                    x = pointX + plotLeft - labelWidth - horizontalOffset;
                } else {
                    x = pointX + plotLeft + horizontalOffset;
                }

                const y = 24;

                return { x, y };
            },
        },
        annotations: annotations.length > 0 ? annotations : undefined,
    } satisfies Options;
};
